(() => {
  const SELECTOR = '.mc-featured slider-component';
  const WIDTH_TOLERANCE = 1;

  class MeisterInfiniteFeaturedCarousel {
    constructor(component) {
      if (!component || component.dataset.mcInfiniteReady === 'true') return;

      this.component = component;
      this.slider = component.querySelector('[id^="Slider-"]');
      this.prevButton = component.querySelector('button[name="previous"]');
      this.nextButton = component.querySelector('button[name="next"]');
      this.originalSlides = this.slider
        ? Array.from(this.slider.querySelectorAll(':scope > [id^="Slide-"]'))
        : [];
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.resizeTimer = null;
      this.scrollRaf = null;
      this.isNormalizing = false;
      this.lastObservedWidth = 0;

      if (!this.slider || this.originalSlides.length < 2) return;

      this.component.dataset.mcInfiniteReady = 'true';
      this.component.classList.add('mc-featured-loop');
      this.component.style.overflowAnchor = 'none';
      this.slider.style.overflowAnchor = 'none';

      this.onPrevClick = (event) => this.onArrowClick(event, -1);
      this.onNextClick = (event) => this.onArrowClick(event, 1);
      this.onScroll = () => this.handleScroll();
      this.onScrollEnd = () => this.normalizeLoopPosition();
      this.onResize = (entries) => {
        const entry = entries?.[0];
        const nextWidth = entry?.contentRect?.width || this.slider.clientWidth;

        if (!nextWidth) return;
        if (this.lastObservedWidth && Math.abs(nextWidth - this.lastObservedWidth) < WIDTH_TOLERANCE) return;

        this.lastObservedWidth = nextWidth;
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.rebuild(), 120);
      };

      this.build();
      this.bind();
    }

    measureStep() {
      const visibleSlides = this.originalSlides.filter((slide) => slide.clientWidth > 0);
      if (visibleSlides.length < 2) return 0;
      return visibleSlides[1].offsetLeft - visibleSlides[0].offsetLeft;
    }

    getLeadingInset() {
      const firstChild = this.slider.firstElementChild;
      return firstChild ? firstChild.offsetLeft : 0;
    }

    cloneSlide(slide) {
      const clone = slide.cloneNode(true);
      clone.dataset.mcLoopClone = 'true';
      clone.removeAttribute('id');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.style.pointerEvents = 'none';

      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('[aria-controls]').forEach((element) => element.removeAttribute('aria-controls'));
      clone.querySelectorAll('[aria-describedby]').forEach((element) => element.removeAttribute('aria-describedby'));
      clone.querySelectorAll('[form]').forEach((element) => element.removeAttribute('form'));
      clone.querySelectorAll('[data-id]').forEach((element) => {
        if (element.classList.contains('jdgm-widget')) element.removeAttribute('data-id');
      });

      clone.querySelectorAll('product-form').forEach((productForm) => {
        const replacement = document.createElement('div');
        replacement.className = productForm.className;
        Array.from(productForm.attributes).forEach((attribute) => {
          if (attribute.name !== 'class' && !attribute.name.startsWith('data-')) return;
          if (attribute.name !== 'class') replacement.setAttribute(attribute.name, attribute.value);
        });
        while (productForm.firstChild) replacement.appendChild(productForm.firstChild);
        productForm.replaceWith(replacement);
      });

      clone.querySelectorAll('a, button, input, select, textarea, form').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });

      return clone;
    }

    build(anchorSlide = this.originalSlides[0]) {
      this.removeClones();
      this.step = this.measureStep();
      if (!this.step) return;

      const visibleCount = Math.max(1, Math.ceil(this.slider.clientWidth / this.step));
      this.bufferCount = Math.min(this.originalSlides.length, Math.max(4, visibleCount + 2));

      const beforeSlides = this.originalSlides.slice(-this.bufferCount);
      const afterSlides = this.originalSlides.slice(0, this.bufferCount);
      const beforeFragment = document.createDocumentFragment();
      const afterFragment = document.createDocumentFragment();

      beforeSlides.forEach((slide) => beforeFragment.appendChild(this.cloneSlide(slide)));
      afterSlides.forEach((slide) => afterFragment.appendChild(this.cloneSlide(slide)));

      this.slider.insertBefore(beforeFragment, this.slider.firstChild);
      this.slider.appendChild(afterFragment);

      const loopClones = Array.from(this.slider.querySelectorAll(':scope > [data-mc-loop-clone="true"]'));
      this.beforeClones = loopClones.slice(0, this.bufferCount);
      this.afterClones = loopClones.slice(this.bufferCount);

      this.leadingInset = this.getLeadingInset();
      this.firstOriginal = this.originalSlides[0];
      this.firstAfterClone = this.afterClones[0];
      this.loopSpan = this.firstAfterClone.offsetLeft - this.firstOriginal.offsetLeft;
      this.canonicalStart = this.firstOriginal.offsetLeft - this.leadingInset;
      this.canonicalEnd = this.firstAfterClone.offsetLeft - this.leadingInset;
      this.lastObservedWidth = this.slider.getBoundingClientRect().width || this.slider.clientWidth;

      const target = anchorSlide && this.originalSlides.includes(anchorSlide) ? anchorSlide : this.firstOriginal;
      this.jumpTo(target.offsetLeft - this.leadingInset);
      this.keepButtonsEnabled();
    }

    removeClones() {
      if (!this.slider) return;
      this.slider.querySelectorAll(':scope > [data-mc-loop-clone="true"]').forEach((clone) => clone.remove());
    }

    bind() {
      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrevClick, true);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNextClick, true);
      this.slider.addEventListener('scroll', this.onScroll, { passive: true });

      if ('onscrollend' in window) {
        this.slider.addEventListener('scrollend', this.onScrollEnd, { passive: true });
      }

      this.buttonObserver = new MutationObserver(() => this.keepButtonsEnabled());
      [this.prevButton, this.nextButton].filter(Boolean).forEach((button) => {
        this.buttonObserver.observe(button, { attributes: true, attributeFilter: ['disabled'] });
      });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.slider);
      } else {
        window.addEventListener('resize', this.onWindowResizeFallback, { passive: true });
      }
    }

    onWindowResizeFallback = () => {
      const nextWidth = this.slider.clientWidth;
      if (!nextWidth || Math.abs(nextWidth - this.lastObservedWidth) < WIDTH_TOLERANCE) return;

      this.lastObservedWidth = nextWidth;
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => this.rebuild(), 120);
    };

    nearestOriginalSlide() {
      const viewportStart = this.slider.scrollLeft + (this.leadingInset || 0);
      return this.originalSlides.reduce((nearest, slide) => {
        if (!nearest) return slide;
        return Math.abs(slide.offsetLeft - viewportStart) < Math.abs(nearest.offsetLeft - viewportStart)
          ? slide
          : nearest;
      }, null);
    }

    rebuild() {
      if (!this.slider || !this.step) return;

      const anchor = this.nearestOriginalSlide();
      const verticalPosition = window.scrollY;
      const horizontalPosition = window.scrollX;

      this.build(anchor);

      window.requestAnimationFrame(() => {
        if (Math.abs(window.scrollY - verticalPosition) > 1) {
          window.scrollTo({ left: horizontalPosition, top: verticalPosition, behavior: 'auto' });
        }
      });
    }

    onArrowClick(event, direction) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.keepButtonsEnabled();

      if (!this.step) return;
      this.slider.scrollBy({
        left: direction * this.step,
        behavior: this.reduceMotion.matches ? 'auto' : 'smooth',
      });
    }

    handleScroll() {
      this.keepButtonsEnabled();
      if (this.scrollRaf) return;

      this.scrollRaf = window.requestAnimationFrame(() => {
        this.scrollRaf = null;
        if (!this.step || !this.beforeClones?.length || !this.afterClones?.length) return;

        const physicalLeftGuard = this.beforeClones[0].offsetLeft + this.step * 0.75;
        const lastAfterClone = this.afterClones[this.afterClones.length - 1];
        const physicalRightGuard = lastAfterClone.offsetLeft - this.slider.clientWidth + this.step * 0.25;

        if (this.slider.scrollLeft <= physicalLeftGuard) {
          this.jumpTo(this.slider.scrollLeft + this.loopSpan);
        } else if (this.slider.scrollLeft >= physicalRightGuard) {
          this.jumpTo(this.slider.scrollLeft - this.loopSpan);
        }
      });
    }

    normalizeLoopPosition() {
      if (!this.loopSpan || this.isNormalizing) return;

      const tolerance = this.step * 0.35;
      if (this.slider.scrollLeft < this.canonicalStart - tolerance) {
        this.jumpTo(this.slider.scrollLeft + this.loopSpan);
      } else if (this.slider.scrollLeft >= this.canonicalEnd - tolerance) {
        this.jumpTo(this.slider.scrollLeft - this.loopSpan);
      }
    }

    jumpTo(position) {
      if (!Number.isFinite(position)) return;
      this.isNormalizing = true;
      const previousBehavior = this.slider.style.scrollBehavior;
      this.slider.style.scrollBehavior = 'auto';
      this.slider.scrollLeft = position;
      this.slider.style.scrollBehavior = previousBehavior;
      window.requestAnimationFrame(() => {
        this.isNormalizing = false;
        this.keepButtonsEnabled();
      });
    }

    keepButtonsEnabled() {
      if (this.prevButton?.hasAttribute('disabled')) this.prevButton.removeAttribute('disabled');
      if (this.nextButton?.hasAttribute('disabled')) this.nextButton.removeAttribute('disabled');
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((component) => new MeisterInfiniteFeaturedCarousel(component));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
